import React from "react";
import { Link } from "react-router-dom";
import { categories } from "../data/tours";

/**
 * A tour, as a photograph with words under it.
 *
 * Portrait rather than landscape, because a 3:4 frame of a mountain valley
 * carries further at card size than a letterbox does, and because a row of tall
 * cards reads as a catalogue rather than as a dashboard.
 *
 * No card border, no drop shadow, no rounded corners on the panel. Everything
 * that used to sit ON the image — a coloured category pill, a rating chip — is
 * either gone or moved into the text, so the picture is uninterrupted. The
 * whole card is one link; it used to be a card with a button in the corner,
 * which gave the same destination two targets and made the picture inert.
 */

export default function TourCard({ tour }) {
  const value = Array.isArray(tour.category) ? tour.category[0] : tour.category;
  const category = categories.find((c) => c.value === value)?.label || value;

  return (
    <Link to={`/tours/${tour.slug || tour.id}`} className="group block">
      <div className="relative overflow-hidden bg-primary-100 aspect-[3/4]">
        <img
          src={tour.cardImage || tour.image}
          alt={tour.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
      </div>

      <div className="pt-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-600">
          {category}
        </p>
        <h3 className="font-display text-xl md:text-2xl font-normal leading-snug text-primary-900 mt-2 group-hover:text-accent-700 transition-colors">
          {tour.title}
        </h3>
        <p className="text-primary-500 text-sm leading-relaxed mt-3 line-clamp-2">
          {tour.shortDesc}
        </p>

        {/* The facts on one rule-separated line: where, how long, what it costs. */}
        <div className="mt-5 pt-4 border-t border-primary-200 flex items-baseline justify-between gap-4">
          <span className="text-xs uppercase tracking-wider text-primary-400">
            {tour.duration}
          </span>
          <span className="text-sm text-primary-800">
            {typeof tour.price === "number" ? (
              <>
                <span className="text-primary-400">from </span>
                ${tour.price.toLocaleString()}
              </>
            ) : (
              tour.priceNote || "On request"
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}
